/**
 * The Copyrights service implementation provides a full copyrights notice in a HTML format that can be displayed to
 * the user when the copyrights link is clicked.
 *
 * The Copyrights API can take as an argument an area which concerns the copyrights. If this argument is omitted
 * the whole world is considered.
 *
 * The API handles the "edge of the world" issue. It allows the proper handling of bounding box that is outside of world
 * extents (-180, -90, 180, 90).
 *
 * Parameters need to be passed to the constructor.
 *
 * ### Response
 * The final response from the service is a HTML string with the copyrights information that have to
 * be displayed.
 * The whole respose is wrapped into a `div` element with the id `copyrightMessage`. Country names are
 * enclosed within `h4` elements and the copyrights information per each supplier is enclosed in `p`
 * elements. This makes styling with CSS fairly easy. An example response may look like this:
 *```html
 * <div id="copyrightMessage"><h4>General Copyrights:</h4>
 *   <p>© 1992 – 2018 TomTom. All rights reserved. This
 *   material is proprietary and the subject of
 *   copyrights protection, database right protection
 *   and other intellectual property rights owned
 *   by TomTom or its suppliers. The use of
 *   this material is subject to the terms of a license
 *   agreement. Any unauthorized copying or disclosure
 *   of this material will lead to criminal and civil
 *   liabilities.</p>
 *   <p>Data Source © 2018 TomTom</p>
 * </div>
 * ```
 *
 *
 * @example
 * ```js
 * //a bounding box copyrights call
 * tt.services.copyrights({
 *   key: <Your API key>,
 *   boundingBox: '0, 0, 1, 1'
 * }).then(function(response) {
 *     console.log(response);
 *   });
 *
 * //a tile-level copyrights call
 * tt.services.copyrights({
 *   key: <Your API key>,
 *   zoom: 1,
 *   x: 1,
 *   y: 1
 * }).then(function(response) {
 *     console.log(response);
 *   });
 * ```
 *
 * @class copyrights
 * @namespace Services.services
 * @module Services
 * @constructor
 * @uses KeyMixin
 * @uses BoundingBoxMixin
 * @uses ProtocolMixin
 * @uses ZoomMixin
 * @uses XMixin
 * @uses YMixin
 *
 * @param {Object} [options]
 *
 */

import {SERVICE_TYPES} from 'Core/serviceTypes';
import { copyrightsRest } from 'rest/copyrights';
import copyrightsParameters from 'services/copyrights/copyrightsParameters';
import serviceFactory from '../serviceFactory';

const fields = copyrightsParameters.create();

function copyrightsQuery(fields, options) {
    if (options.boundingBox) {
        return copyrightsRest.copyrightsBounds(fields, options);
    } else if (options.zoom) {
        return copyrightsRest.copyrightsZoom(fields, options);
    } else {
        return copyrightsRest.copyrightsWorld(fields, options);
    }
}

function handleServiceCall(requestOptions) {
    return copyrightsQuery(fields, requestOptions);
}

export const copyrights = serviceFactory(fields, SERVICE_TYPES.MAP, 'copyrights', handleServiceCall);
